import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'

// ─── Features / How it works ─────────────────────────────────────────────────
//
// Deep-dive into what Receipt Chaser actually does, grouped by job-to-be-done.
// Designed to:
//   - Help genuinely-interested bookkeepers understand the workflow
//   - Pick up organic search for UK bookkeeping receipt-collection queries
//   - Set honest expectations about what the product is and isn't
//
// SEO-relevant phrases woven into the natural copy: "receipt collection
// software", "chase clients for receipts", "missing receipts", "wholly
// business", "UK bookkeeping", "request receipts from clients".

const TABS = [
  { id: 'sending',   label: 'Sending requests' },
  { id: 'receiving', label: 'Receiving receipts' },
  { id: 'practice',  label: 'Behind the scenes' },
]

// ─── Reusable bits ───────────────────────────────────────────────────────────

function SectionHeading({ eyebrow, title, body }) {
  return (
    <div style={{ marginBottom: 40, maxWidth: 640 }}>
      {eyebrow && (
        <p style={{
          fontSize: '0.78rem', fontWeight: 600,
          color: 'var(--accent)', textTransform: 'uppercase',
          letterSpacing: '0.05em', marginBottom: 10,
        }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{ marginBottom: 14, lineHeight: 1.2 }}>{title}</h2>
      {body && (
        <p style={{ color: 'var(--ink-muted)', fontSize: '1rem', lineHeight: 1.6 }}>{body}</p>
      )}
    </div>
  )
}

// Each feature row: text on one side, mock screenshot on the other.
// `reverse` flips the order on desktop. On mobile (single column) we
// always show text first, then mock — regardless of `reverse` — so the
// page reads in a consistent rhythm: heading, copy, screenshot, repeat.
function FeatureRow({ eyebrow, title, body, mock, reverse }) {
  return (
    <div
      className="feature-row"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 48, alignItems: 'center',
        marginBottom: 72,
      }}
    >
      <div className="feature-row__text" data-reverse={reverse ? 'true' : 'false'}>
        {eyebrow && (
          <p style={{
            fontSize: '0.72rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 10,
          }}>
            {eyebrow}
          </p>
        )}
        <h3 style={{ fontSize: '1.45rem', marginBottom: 14, lineHeight: 1.25 }}>{title}</h3>
        <div style={{ color: 'var(--ink-muted)', fontSize: '0.95rem', lineHeight: 1.65 }}>
          {body}
        </div>
      </div>
      <div className="feature-row__mock" data-reverse={reverse ? 'true' : 'false'}>
        {mock}
      </div>
    </div>
  )
}

// Inline stylesheet for FeatureRow ordering and the tab bar's
// responsive behaviour. Kept inside this file so the component is
// self-contained.
//
//   FeatureRow: mobile (single-column) always renders text above mock;
//   desktop honours the `reverse` flag to alternate sides visually.
//
//   Tab bar: desktop centres the tabs (all three fit). Mobile scrolls
//   horizontally with a fade gradient on the right edge as a scroll
//   cue, so users can see "Behind the scenes" is reachable.
const FeatureRowStyles = () => (
  <style>{`
    /* ── FeatureRow ─────────────────────────────────────────────── */
    /* Mobile / narrow widths — always text first, then mock. */
    .feature-row__text { order: 1; }
    .feature-row__mock { order: 2; }

    /* Desktop — honour the reverse flag to alternate sides visually. */
    @media (min-width: 720px) {
      .feature-row__text[data-reverse="true"]  { order: 2; }
      .feature-row__mock[data-reverse="true"]  { order: 1; }
      .feature-row__text[data-reverse="false"] { order: 1; }
      .feature-row__mock[data-reverse="false"] { order: 2; }
    }

    /* ── Tab bar ────────────────────────────────────────────────── */
    /* Mobile (default): scrollable, left-aligned, with right-edge
       fade gradient as a visual scroll cue. */
    .features-tabs-wrap {
      position: relative;
      margin: 0 -24px;
    }
    .features-tabs {
      display: flex;
      gap: 4px;
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      -webkit-overflow-scrolling: touch;
      padding: 12px 24px;
    }
    .features-tabs::-webkit-scrollbar { display: none; }

    /* Right-edge fade: ~32px wide, fades from transparent to the
       sticky bar's solid colour (#f7f6f2 = var(--surface)). pointer-
       events: none so it doesn't block taps on the last tab. */
    .features-tabs-wrap::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 32px;
      height: 100%;
      pointer-events: none;
      background: linear-gradient(
        to right,
        rgba(247, 246, 242, 0) 0%,
        rgba(247, 246, 242, 0.95) 100%
      );
    }

    /* Desktop: centre the tabs and hide the fade — all three fit
       comfortably so there's nothing to scroll. */
    @media (min-width: 720px) {
      .features-tabs {
        justify-content: center;
      }
      .features-tabs-wrap::after {
        display: none;
      }
    }
  `}</style>
)

// ─── Mock screenshots ────────────────────────────────────────────────────────
// All CSS-based, no real images. They mirror the actual app's design so
// visitors can recognise what they'd be using.

function MockShell({ children, title }) {
  return (
    <div style={{
      background: 'var(--white)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid var(--surface-2)',
      padding: '14px 18px',
      maxWidth: 460, margin: '0 auto',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        paddingBottom: 10, borderBottom: '1px solid var(--surface-2)',
        marginBottom: 14,
      }}>
        <div style={{
          width: 22, height: 22, background: 'var(--accent)', borderRadius: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: '0.7rem',
        }}>📋</div>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.85rem' }}>
          Receipt Chaser
        </span>
        {title && (
          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--ink-faint)' }}>
            {title}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

function MockTransactionList() {
  const rows = [
    { d: '02 Mar', n: 'Mistral.ai',       a: '-£13.04', s: 'done',    f: 'Mistral_£13.04.pdf' },
    { d: '04 Mar', n: 'Tesco Express',    a: '-£24.99', s: 'done',    f: 'Tesco_£24.99.jpg' },
    { d: '11 Mar', n: 'OpenAI',           a: '-£17.93', s: 'pending' },
    { d: '14 Mar', n: 'GoDaddy',          a: '-£95.34', s: 'pending' },
    { d: '18 Mar', n: 'Amazon UK',        a: '-£42.10', s: 'lost'    },
  ]
  return (
    <MockShell title="March 2026">
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.85rem' }}>5 receipts requested</div>
      <div style={{ color: 'var(--ink-muted)', fontSize: '0.7rem', marginBottom: 10 }}>2 done · 2 pending · 1 lost</div>
      <div style={{ height: 4, background: 'var(--surface-2)', borderRadius: 100, marginBottom: 14 }}>
        <div style={{ height: '100%', width: '40%', background: 'var(--accent)', borderRadius: 100 }} />
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '7px 0',
          borderBottom: i < rows.length - 1 ? '1px solid var(--surface-2)' : 'none',
          fontSize: '0.78rem',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 2,
            background:
              r.s === 'done' ? 'var(--green-light)' :
              r.s === 'lost' ? 'rgba(220, 38, 38, 0.1)' : 'var(--surface-2)',
            color:
              r.s === 'done' ? 'var(--green)' :
              r.s === 'lost' ? '#dc2626' : 'var(--ink-faint)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.6rem', fontWeight: 700,
          }}>{r.s === 'done' ? '✓' : r.s === 'lost' ? '!' : ''}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--ink-faint)', fontSize: '0.65rem' }}>{r.d}</div>
            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.n}
            </div>
            {r.f && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                marginTop: 3, padding: '1px 6px', borderRadius: 4,
                background: 'var(--green-light)', color: 'var(--green)',
                fontSize: '0.6rem', fontWeight: 500,
                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                📎 {r.f}
              </div>
            )}
          </div>
          <div style={{ fontWeight: 600, fontSize: '0.72rem', marginTop: 2 }}>{r.a}</div>
        </div>
      ))}
    </MockShell>
  )
}

function MockClientView() {
  return (
    <MockShell title="What your client sees">
      <div style={{ fontSize: '0.78rem', color: 'var(--ink)' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Receipts needed for March</div>
        <div style={{ color: 'var(--ink-muted)', fontSize: '0.7rem', marginBottom: 16 }}>
          From John Smith Bookkeeping
        </div>
        {[
          { d: '11 Mar', n: 'OpenAI',  a: '£17.93' },
          { d: '14 Mar', n: 'GoDaddy', a: '£95.34' },
        ].map((r, i) => (
          <div key={i} style={{
            padding: '10px 12px', marginBottom: 8,
            borderRadius: 'var(--radius)', border: '1px solid var(--surface-2)',
            background: 'var(--surface)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--ink-faint)', fontSize: '0.65rem' }}>{r.d}</div>
              <div style={{ fontWeight: 500, fontSize: '0.78rem' }}>{r.n}</div>
              <div style={{ color: 'var(--ink-muted)', fontSize: '0.72rem' }}>{r.a}</div>
            </div>
            <button style={{
              background: 'var(--accent)', color: 'white', border: 'none',
              padding: '5px 10px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 500,
              cursor: 'default',
            }}>
              📷 Upload
            </button>
          </div>
        ))}
        <div style={{
          padding: '10px 12px', borderRadius: 'var(--radius)',
          border: '1px dashed var(--surface-3)', textAlign: 'center',
          color: 'var(--ink-faint)', fontSize: '0.72rem',
        }}>
          + Drag receipts here to upload all at once
        </div>
      </div>
    </MockShell>
  )
}

function MockDownload() {
  return (
    <MockShell title="Downloaded zip">
      <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'var(--ink)' }}>
        <div style={{ color: 'var(--ink-muted)', marginBottom: 8 }}>📁 receipts-march-2026.zip</div>
        {[
          '2026-03-02_Mistral_£13.04.pdf',
          '2026-03-04_Tesco_£24.99.jpg',
          '2026-03-11_OpenAI_£17.93.pdf',
          '2026-03-14_GoDaddy_£95.34.pdf',
          '2026-03-18_Amazon_£42.10.txt',
          'summary.csv',
        ].map((f, i) => (
          <div key={i} style={{
            padding: '6px 12px', borderRadius: 4,
            background: i % 2 ? 'var(--surface)' : 'transparent',
            fontSize: '0.72rem',
          }}>
            {f.endsWith('.csv') ? '📊' : f.endsWith('.txt') ? '📝' : '📎'} {f}
          </div>
        ))}
      </div>
    </MockShell>
  )
}

function MockTeam() {
  const members = [
    { name: 'Sarah Wilson',  role: 'Owner',    initial: 'S', color: '#489291' },
    { name: 'James Patel',   role: 'Member',   initial: 'J', color: '#d97706' },
    { name: 'Aisha Khan',    role: 'Member',   initial: 'A', color: '#4a6cf7' },
  ]
  return (
    <MockShell title="Your practice">
      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 12 }}>
        Team members (3)
      </div>
      {members.map((m, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 0',
          borderBottom: i < members.length - 1 ? '1px solid var(--surface-2)' : 'none',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 100,
            background: m.color, color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 600, fontSize: '0.78rem',
          }}>{m.initial}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>{m.name}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--ink-muted)' }}>{m.role}</div>
          </div>
        </div>
      ))}
      <button style={{
        marginTop: 14, width: '100%',
        padding: '8px 12px',
        background: 'transparent', color: 'var(--accent)',
        border: '1px dashed var(--surface-3)', borderRadius: 6,
        fontSize: '0.78rem', fontWeight: 500, cursor: 'default',
      }}>
        + Invite a colleague
      </button>
    </MockShell>
  )
}

function MockLostReceipt() {
  return (
    <MockShell title="Lost receipt declaration">
      <div style={{ fontSize: '0.78rem' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>18 Mar · Amazon UK · £42.10</div>
        <div style={{
          padding: '8px 10px', marginBottom: 10,
          borderRadius: 6, background: 'rgba(220, 38, 38, 0.05)',
          color: '#dc2626', fontSize: '0.72rem', fontWeight: 500,
        }}>
          ! Marked as lost by client
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginBottom: 6 }}>
          Explanation
        </div>
        <div style={{
          padding: '8px 10px', borderRadius: 6,
          background: 'var(--surface)', fontSize: '0.74rem',
          color: 'var(--ink)', marginBottom: 10, lineHeight: 1.5,
        }}>
          Office supplies for the studio: printer paper, biros, sticky notes.
          Bin liner ripped, receipt was on the bottom and got wet.
        </div>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: '0.72rem', color: 'var(--ink)',
        }}>
          <span style={{
            width: 14, height: 14, borderRadius: 3,
            background: 'var(--accent)', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.6rem',
          }}>✓</span>
          Wholly &amp; exclusively for business use
        </label>
      </div>
    </MockShell>
  )
}

// ─── The tab content ─────────────────────────────────────────────────────────

function SendingContent() {
  return (
    <>
      <SectionHeading
        eyebrow="Sending requests"
        title="Tell your client exactly which receipts and invoices you need, transaction by transaction."
        body="Most receipt-chasing tools assume your client knows what's missing. They don't. Receipt Chaser starts from your accounting data and turns each transaction needing a receipt or invoice into a specific, labelled item your client can act on."
      />

      <FeatureRow
        eyebrow="See everything at a glance"
        title="All your clients on one screen"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              The dashboard shows every client you've sent a request to,
              what's outstanding, and what's been completed. At month-end
              you can see in seconds which clients still owe you receipts
              and which are done.
            </p>
            <p>
              No more spreadsheets tracking who you've chased, no more
              flicking through emails to remember whose paperwork came in
              and whose didn't.
            </p>
          </>
        }
        mock={
          <MockShell title="Your clients">
            <div style={{ fontSize: '0.78rem' }}>
              {[
                { n: 'Acme Studio Ltd',     u: 8, t: 8,  status: 'done'    },
                { n: 'Beech & Co',          u: 5, t: 12, status: 'pending' },
                { n: 'Carter Plumbing',     u: 3, t: 14, status: 'pending' },
                { n: 'Delta Marketing Ltd', u: 0, t: 0,  status: 'none'    },
                { n: 'Evergreen Café',      u: 6, t: 6,  status: 'done'    },
              ].map((c, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 0',
                  borderBottom: i < 4 ? '1px solid var(--surface-2)' : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.78rem' }}>{c.n}</div>
                    {c.t > 0 && (
                      <div style={{ color: 'var(--ink-faint)', fontSize: '0.66rem', marginTop: 1 }}>
                        {c.u} of {c.t} receipts uploaded
                      </div>
                    )}
                  </div>
                  {c.status === 'done' && (
                    <span style={{
                      padding: '2px 8px', borderRadius: 4,
                      background: 'var(--green-light)', color: 'var(--green)',
                      fontSize: '0.65rem', fontWeight: 600,
                    }}>Complete</span>
                  )}
                  {c.status === 'pending' && (
                    <span style={{
                      padding: '2px 8px', borderRadius: 4,
                      background: 'rgba(72, 146, 145, 0.1)', color: 'var(--accent)',
                      fontSize: '0.65rem', fontWeight: 600,
                    }}>{c.t - c.u} outstanding</span>
                  )}
                  {c.status === 'none' && (
                    <span style={{
                      padding: '2px 8px', borderRadius: 4,
                      background: 'var(--surface-2)', color: 'var(--ink-faint)',
                      fontSize: '0.65rem', fontWeight: 500,
                    }}>No active request</span>
                  )}
                </div>
              ))}
            </div>
          </MockShell>
        }
      />

      <FeatureRow
        eyebrow="From any UK source"
        title="Import from Xero, Sage, QuickBooks or any bank CSV"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              Receipt Chaser reads CSVs from the major UK accounting platforms
              and from bank exports. We map the columns automatically (date,
              description, amount, payee) and pull through the right fields
              for each source.
            </p>
            <p style={{ marginBottom: 12 }}>
              Where the source has a clean payee field (Xero, QuickBooks), we
              use that. Where it doesn't (Sage, raw bank exports), we extract
              a clean vendor name from the description so your client sees
              "OpenAI" instead of "INT'L 0039022552 OPENAI *CHATGPT SU..."
            </p>
          </>
        }
        mock={<MockTransactionList />}
        reverse
      />

      <FeatureRow
        eyebrow="Pick what you actually need"
        title="Tick the transactions you want receipts or invoices for, skip the rest"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              Not every transaction needs a receipt. Direct debits, payroll,
              transfers. Your client doesn't need to send anything for those.
              You pick which transactions actually need supporting paperwork
              and only those land on your client's list.
            </p>
            <p>
              You can add a note to the whole request explaining context, or a
              specific note on individual transactions for anything unusual.
            </p>
          </>
        }
        mock={
          <MockShell title="Build a request">
            <div style={{ fontSize: '0.78rem' }}>
              <div style={{ marginBottom: 8, color: 'var(--ink-muted)' }}>
                12 transactions imported · 5 need receipts
              </div>
              {[
                { ck: true,  n: 'Mistral.ai',     a: '-£13.04' },
                { ck: true,  n: 'Tesco Express',  a: '-£24.99' },
                { ck: false, n: 'Direct debit, HMRC', a: '-£820.00' },
                { ck: true,  n: 'OpenAI',         a: '-£17.93' },
                { ck: false, n: 'Payroll',        a: '-£3200.00' },
              ].map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 0',
                  borderBottom: i < 4 ? '1px solid var(--surface-2)' : 'none',
                  opacity: r.ck ? 1 : 0.55,
                }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: 3,
                    background: r.ck ? 'var(--accent)' : 'var(--white)',
                    border: r.ck ? 'none' : '1px solid var(--surface-3)',
                    color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
                  }}>{r.ck ? '✓' : ''}</div>
                  <span style={{ flex: 1, fontSize: '0.76rem' }}>{r.n}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{r.a}</span>
                </div>
              ))}
            </div>
          </MockShell>
        }
      />

      <FeatureRow
        eyebrow="One link, no account"
        title="Your client just opens a link"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              No accounts, no passwords, no app to install. We send your
              client an email with a private link. They open it on their
              phone, tablet or laptop, whatever's nearest, and see
              exactly the list you sent them.
            </p>
            <p style={{ marginBottom: 12 }}>
              The email itself is completely customisable. Use your own
              wording, your own tone, address them by name. It can read
              like a personal note rather than a system-generated
              notification, which makes a real difference to whether your
              client opens it and acts on it.
            </p>
            <p>
              Lower friction means higher response rates. Your client
              doesn't need an excuse like "I forgot my password" to delay.
            </p>
          </>
        }
        mock={<MockClientView />}
        reverse
      />
    </>
  )
}

function ReceivingContent() {
  return (
    <>
      <SectionHeading
        eyebrow="Receiving receipts"
        title="Receipts arrive matched, named and ready to file."
        body="The job doesn't end when the receipts come in. Each one needs to be matched to the right transaction, named so you can find it later, and properly handled if it's been lost. Receipt Chaser does most of this automatically."
      />

      <FeatureRow
        eyebrow="Drag, drop, match"
        title="Bulk upload with smart matching"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              Your client doesn't need to upload receipts one at a time
              against each line. They drop everything in at once, and we
              suggest the right matches based on dates and amounts in the
              filename or filename metadata.
            </p>
            <p>
              If our suggestion is wrong, they can drag the receipt onto a
              different transaction. On mobile, they can just tap to assign.
            </p>
          </>
        }
        mock={
          <MockShell title="Bulk upload">
            <div style={{ fontSize: '0.78rem' }}>
              <div style={{
                padding: '14px 12px', marginBottom: 14,
                borderRadius: 8, border: '2px dashed var(--accent)',
                background: 'rgba(72, 146, 145, 0.04)', textAlign: 'center',
                color: 'var(--accent)', fontSize: '0.82rem', fontWeight: 500,
              }}>
                📤 5 receipts uploaded, drag onto a transaction
              </div>
              {[
                { n: 'Mistral.ai', f: 'mistral-invoice.pdf', match: 'high' },
                { n: 'Tesco Express', f: 'tesco-photo.jpg', match: 'high' },
                { n: 'OpenAI', f: '(no match)', match: 'none' },
              ].map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 0',
                  borderBottom: i < 2 ? '1px solid var(--surface-2)' : 'none',
                }}>
                  <span style={{ fontSize: '0.74rem', flex: 1 }}>{r.n}</span>
                  {r.match === 'high' ? (
                    <span style={{
                      padding: '2px 7px', borderRadius: 4,
                      background: 'var(--green-light)', color: 'var(--green)',
                      fontSize: '0.65rem', fontWeight: 500,
                    }}>📎 {r.f}</span>
                  ) : (
                    <span style={{
                      padding: '2px 7px', borderRadius: 4,
                      background: 'var(--surface-2)', color: 'var(--ink-faint)',
                      fontSize: '0.65rem',
                    }}>Drag here</span>
                  )}
                </div>
              ))}
            </div>
          </MockShell>
        }
      />

      <FeatureRow
        eyebrow="HMRC-friendly handling"
        title="Lost receipts handled properly"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              Sometimes a receipt or invoice genuinely can't be found.
              Rather than leaving you with a gap, your client can mark a
              transaction as "lost" and provide a written explanation, plus
              tick a "wholly and exclusively for business" declaration where
              applicable.
            </p>
            <p style={{ marginBottom: 12 }}>
              This is what HMRC expects for any expense without supporting
              paperwork: an audit trail explaining what it was for and
              confirming it was a legitimate business cost.
            </p>
            <p>
              Each lost declaration can be exported as a printable PDF for
              your records, and the explanation also appears in the CSV
              export alongside the transaction.
            </p>
          </>
        }
        mock={<MockLostReceipt />}
        reverse
      />

      <FeatureRow
        eyebrow="Ready to file"
        title="Renamed receipts in a single zip"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              When you download, every file gets renamed automatically to
              <code style={{
                fontFamily: 'monospace', fontSize: '0.85em',
                background: 'var(--surface)', padding: '1px 6px',
                borderRadius: 3, margin: '0 2px',
              }}>YYYY-MM-DD_Vendor_£Amount.ext</code>
              format. Sortable, identifiable, ready to drop into your
              filing system.
            </p>
            <p>
              The zip also includes a <code style={{
                fontFamily: 'monospace', fontSize: '0.85em',
                background: 'var(--surface)', padding: '1px 6px',
                borderRadius: 3, margin: '0 2px',
              }}>summary.csv</code> listing every transaction, what was
              uploaded, what was lost, and any explanations or notes,
              ready for your reconciliation.
            </p>
          </>
        }
        mock={<MockDownload />}
      />
    </>
  )
}

function PracticeContent() {
  return (
    <>
      <SectionHeading
        eyebrow="Behind the scenes"
        title="Built for the way UK bookkeeping practices actually work."
        body="The bits that don't show up in marketing screenshots but make a real difference when you're using something every day."
      />

      <FeatureRow
        eyebrow="Your whole team"
        title="Practice users are unlimited"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              Whether it's just you, or you've got two part-time bookkeepers
              and an apprentice, everyone can have their own login. No
              per-seat pricing, no upgrade tiers. Practice users are
              unlimited on every plan.
            </p>
            <p>
              Each team member can be made an Owner or a Member. Members can
              do everything Owners can, except change billing details and
              invite other users.
            </p>
          </>
        }
        mock={<MockTeam />}
      />

      <FeatureRow
        eyebrow="Internal coordination"
        title="Notes only your team can see"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              Sometimes a transaction needs a flag, like "ask about this at
              the next call", "client said this is personal, double-check",
              "VAT-able?" Your client doesn't need to see those.
            </p>
            <p>
              Add internal notes against any transaction. Visible to your
              whole practice, invisible to the client. They flow through
              to the CSV export so they're not lost when the request is
              done.
            </p>
          </>
        }
        mock={
          <MockShell title="Transaction notes">
            <div style={{ fontSize: '0.78rem' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>11 Mar · OpenAI · £17.93</div>
              <div style={{
                marginTop: 10, padding: '8px 10px',
                background: 'rgba(20, 121, 113, 0.08)',
                borderLeft: '3px solid var(--green)',
                borderRadius: 4, fontSize: '0.72rem',
                color: 'var(--ink-muted)',
              }}>
                🗒 <strong style={{ color: 'var(--ink)' }}>Internal note:</strong> Check
                if this is the new business or personal subscription.
                Client mentioned both at January meeting.
              </div>
              <div style={{
                marginTop: 8, padding: '8px 10px',
                background: 'rgba(74, 108, 247, 0.06)',
                borderLeft: '3px solid var(--blue, #4a6cf7)',
                borderRadius: 4, fontSize: '0.72rem',
                color: 'var(--ink-muted)',
              }}>
                💬 <strong style={{ color: 'var(--ink)' }}>Client note:</strong> This
                is for the ChatGPT subscription, business use.
              </div>
            </div>
          </MockShell>
        }
        reverse
      />

      <FeatureRow
        eyebrow="Quiet by design"
        title="Built around UK bookkeeping conventions"
        body={
          <>
            <p style={{ marginBottom: 12 }}>
              £ amounts. UK date formats (DD/MM/YYYY). Sterling parsing for
              imports. The lost-receipt declaration uses the language HMRC
              actually uses ("wholly and exclusively for business").
            </p>
            <p style={{ marginBottom: 12 }}>
              Files stored securely in EU-hosted infrastructure. No selling
              your data. No advertising on top of your clients' financial
              information. No training AI models on what your bookkeepers
              upload.
            </p>
            <p>
              Receipts kept for 7 years aligned with HMRC retention rules,
              so historical records stay accessible if you ever need to
              produce them.
            </p>
          </>
        }
        mock={
          <MockShell title="Settings">
            <div style={{ fontSize: '0.78rem' }}>
              {[
                ['Currency',     '£ Pound sterling'],
                ['Date format',  'DD / MM / YYYY'],
                ['File storage', 'EU (Frankfurt)'],
                ['Retention',    '7 years'],
                ['Data sold?',   'Never'],
                ['Data used to train AI?', 'Never'],
              ].map(([k, v], i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '7px 0',
                  borderBottom: i < 5 ? '1px solid var(--surface-2)' : 'none',
                }}>
                  <span style={{ color: 'var(--ink-muted)' }}>{k}</span>
                  <span style={{ fontWeight: 500 }}>{v}</span>
                </div>
              ))}
            </div>
          </MockShell>
        }
      />
    </>
  )
}

// ─── Who it's for / who it isn't for ─────────────────────────────────────────

function WhoItsFor() {
  return (
    <section style={{ padding: '80px 0', background: 'var(--white)' }}>
      <div className="container" style={{ padding: '0 24px', maxWidth: 720 }}>
        <SectionHeading
          eyebrow="Honest pitch"
          title="Is Receipt Chaser right for your practice?"
          body="It's built for a specific kind of bookkeeping work. Here's the type of practice it fits best."
        />

        <div style={{
          background: 'var(--surface)',
          padding: '32px 32px 36px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--surface-2)',
          borderTop: '3px solid var(--green)',
        }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: 16 }}>It's a good fit if you…</h3>
          <ul style={{
            listStyle: 'none', padding: 0,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {[
              'Run an independent UK bookkeeping practice with 5 to 200 clients',
              'Want a simpler, faster way to chase receipts and invoices from clients without long email threads',
              'Spend real time each month chasing missing paperwork and want that time back',
              'Work with clients who tend to send receipts in dribs and drabs rather than all in one go',
              'Need an audit-ready record of which receipts came in, and which were declared lost',
              'Want your clients to use something that "just works" on their phone, tablet or laptop',
              'Have a small team that needs shared access to the same client records',
              'Use Xero, Sage, QuickBooks, FreeAgent, or work directly from bank CSVs',
            ].map((line, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{
                  width: 20, height: 20, borderRadius: 100,
                  background: 'var(--green-light)', color: 'var(--green)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700, marginTop: 1, flexShrink: 0,
                }}>✓</span>
                <span style={{ fontSize: '0.92rem', color: 'var(--ink-muted)', lineHeight: 1.55 }}>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

// ─── Final CTA ───────────────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section style={{ padding: '80px 0' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{
          background: 'var(--ink)',
          borderRadius: 'var(--radius-lg)',
          padding: 'clamp(40px, 6vw, 64px) clamp(28px, 5vw, 64px)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(72,146,145,0.25), transparent 50%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative' }}>
            <h2 style={{ color: 'white', marginBottom: 14 }}>
              See it work with your own data.
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.8)', fontSize: '1.05rem',
              marginBottom: 28, maxWidth: 540, margin: '0 auto 28px',
            }}>
              Start a free 30-day trial. Import your first client's
              transactions and send a real request in under five minutes.
              No card required.
            </p>
            <Link to="/signup" className="btn btn-primary" style={{ padding: '14px 28px', fontSize: '1rem' }}>
              Start your free trial →
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Features() {
  const [activeTab, setActiveTab] = useState('sending')

  // Scroll to top on tab change so the content shift is visible
  // (without forcing it on every interaction, only when tab id changes)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="Features: how Receipt Chaser collects receipts from clients"
        description="See how Receipt Chaser sends transaction-by-transaction receipt requests, lets clients upload by phone or laptop, handles lost receipts, and packages everything into an audit-ready zip."
        path="/features"
      />
      <FeatureRowStyles />
      <TopNav />

      {/* Page intro */}
      <section style={{ padding: '64px 0 32px' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 720, textAlign: 'center',
        }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 12,
          }}>
            Features &amp; how it works
          </p>
          <h1 style={{
            marginBottom: 18, lineHeight: 1.15,
            fontSize: 'clamp(2rem, 5vw, 2.8rem)',
          }}>
            Receipt collection software,<br/>built for UK bookkeepers.
          </h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Receipt Chaser turns your accounting data into a clear
            list of receipts and invoices your client needs to send. They upload directly
            against each transaction. You get a renamed, audit-ready zip when
            they're done. Here's how each part works.
          </p>
        </div>
      </section>

      {/* Tab bar */}
      <div style={{
        position: 'sticky', top: 60, zIndex: 40,
        background: 'rgba(247, 246, 242, 0.85)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--surface-2)',
      }}>
        <div className="container" style={{ padding: '0 24px' }}>
          <div className="features-tabs-wrap">
            <div className="features-tabs">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: activeTab === tab.id ? 'var(--ink)' : 'transparent',
                    color: activeTab === tab.id ? 'white' : 'var(--ink-muted)',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 100,
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'background 0.15s',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Active tab content */}
      <section style={{ padding: '56px 0' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 1080 }}>
          {activeTab === 'sending'   && <SendingContent />}
          {activeTab === 'receiving' && <ReceivingContent />}
          {activeTab === 'practice'  && <PracticeContent />}
        </div>
      </section>

      <WhoItsFor />
      <FinalCTA />
      <Footer />
    </div>
  )
}
